// Script File Viewer Component
// Modal popup to view full script files with syntax highlighting and copy functionality

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Highlight, themes } from 'prism-react-renderer';
import { logger } from '../utils/logger';
import { FloatingSearchButton } from './FloatingSearchButton';
import { useTextSelectionSearch } from '../hooks/useTextSelectionSearch';

interface ScriptFileViewerProps {
  isOpen: boolean;
  onClose: () => void;
  filePath: string;
  lineNumber?: number;
  version?: string;
  branch?: string;
  methodUsageIndex?: Map<string, Set<string>>;
  onSearch?: (searchTerm: string) => void;
}

export const ScriptFileViewer: React.FC<ScriptFileViewerProps> = ({
  isOpen,
  onClose,
  filePath,
  lineNumber,
  version = 'latest',
  branch = 'stable',
  methodUsageIndex = new Map(),
  onSearch
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [fileContent, setFileContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Text selection search functionality (modal-aware)
  const selectionSearch = useTextSelectionSearch({
    usageIndex: methodUsageIndex,
    minLength: 3,
    debounceMs: 50,
    containerRef: modalContentRef,
    zIndexBase: 10001
  });

  // Handle search from text selection
  const handleSelectionSearch = useCallback(() => {
    if (selectionSearch.text && onSearch) {
      onSearch(selectionSearch.text);
      selectionSearch.handleSearch();
      // Close modal after initiating search
      onClose();
    }
  }, [selectionSearch, onSearch, onClose]);

  useEffect(() => {
    // Detect theme and update on change
    const updateTheme = () => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setIsDarkMode(isDark);
    };

    updateTheme();

    // Listen for theme changes
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isOpen && filePath) {
      fetchFileContent();
    }
  }, [isOpen, filePath, version, branch]);

  const fetchFileContent = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch script file with version and branch parameters
      const params = new URLSearchParams({
        path: filePath,
        version: version,
        branch: branch
      });
      const response = await fetch(`/api/scripts/view?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to load script file');
      }

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('GitHub API rate limit exceeded. Please try again in a few minutes, or contact the administrator to add a GITHUB_TOKEN for higher limits.');
        }
        throw new Error(data.error || 'Failed to load script file');
      }

      setFileContent(data.content || '// Script file content not available');
    } catch (err) {
      logger.error('Error loading script:', err);
      setError((err as Error).message || 'Unable to load script file. Please try again later.');
      setFileContent('');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(fileContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getFileName = () => {
    return filePath.split('/').pop() || filePath;
  };

  const getLanguage = () => {
    const ext = filePath.split('.').pop()?.toLowerCase();
    if (ext === 'c') return 'cpp';
    if (ext === 'enforce') return 'cpp';
    return 'cpp';
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-color)',
          borderRadius: '8px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'var(--border-color)'}`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: window.innerWidth < 480 ? '12px 16px' : '16px 20px',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: window.innerWidth < 480 ? 'flex-start' : 'center',
          backgroundColor: 'var(--navbar-bg)',
          gap: '12px',
          flexWrap: window.innerWidth < 480 ? 'wrap' : 'nowrap'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{
              margin: 0,
              fontSize: window.innerWidth < 480 ? '16px' : '18px',
              color: 'var(--text-color)',
              marginBottom: '4px',
              wordBreak: 'break-word'
            }}>
              {getFileName()}
            </h2>
            <p style={{
              margin: 0,
              fontSize: window.innerWidth < 480 ? '11px' : '13px',
              color: 'var(--text-muted)',
              fontFamily: 'monospace',
              marginBottom: '2px',
              wordBreak: 'break-all',
              overflowWrap: 'break-word'
            }}>
              {filePath}
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
              <span style={{ background: 'var(--code-bg)', padding: '2px 6px', borderRadius: '3px', marginRight: '6px' }}>
                {branch === 'stable' ? '🟢 Stable' : '🔴 Experimental'}
              </span>
              <span style={{ background: 'var(--code-bg)', padding: '2px 6px', borderRadius: '3px' }}>
                v{version}
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
            <button
              onClick={copyToClipboard}
              disabled={loading || !!error}
              style={{
                padding: window.innerWidth < 480 ? '6px 12px' : '8px 16px',
                background: copied ? '#28a745' : 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading || error ? 'not-allowed' : 'pointer',
                fontSize: window.innerWidth < 480 ? '12px' : '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'background 0.2s',
                opacity: loading || error ? 0.5 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              {copied ? '✓ Copied!' : '📋 Copy'}
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                padding: '0 8px',
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          ref={modalContentRef}
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: isDarkMode ? '#1e1e1e' : '#f6f8fa',
            position: 'relative'
          }}
        >
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--text-muted)',
              fontSize: '16px'
            }}>
              Loading script file...
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '40px',
              color: 'var(--text-color)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <h3 style={{ color: 'var(--text-color)', marginBottom: '8px' }}>Unable to Load Script</h3>
              <p style={{ color: 'var(--text-muted)', maxWidth: '500px' }}>
                {error}
              </p>
            </div>
          ) : fileContent ? (
            <Highlight
              theme={isDarkMode ? themes.dracula : themes.github}
              code={fileContent}
              language={getLanguage()}
            >
              {({ className, style, tokens, getLineProps, getTokenProps }) => (
                <pre
                  className={className}
                  style={{
                    ...style,
                    margin: 0,
                    padding: '20px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    minHeight: '100%',
                    width: 'fit-content',
                    minWidth: '100%'
                  }}
                >
                  {tokens.map((line, i) => {
                    const lineNum = i + 1;
                    const isHighlighted = lineNumber && lineNum === lineNumber;

                    return (
                      <div
                        key={i}
                        {...getLineProps({ line })}
                        style={{
                          display: 'flex',
                          backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.15)' : 'transparent',
                          padding: '0 8px',
                          margin: '0 -8px'
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: '60px',
                            width: '60px',
                            textAlign: 'right',
                            marginRight: '16px',
                            paddingRight: '8px',
                            userSelect: 'none',
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                            fontWeight: isHighlighted ? 'bold' : 'normal',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {lineNum}
                        </span>
                        <span style={{ flex: 1 }}>
                          {line.map((token, key) => (
                            <span key={key} {...getTokenProps({ token })} />
                          ))}
                        </span>
                      </div>
                    );
                  })}
                </pre>
              )}
            </Highlight>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--text-muted)',
              fontSize: '16px'
            }}>
              No content available
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--border-color)',
          backgroundColor: 'var(--navbar-bg)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '13px',
          color: 'var(--text-muted)'
        }}>
          <div>
            {lineNumber && (
              <span>Highlighted: Line {lineNumber}</span>
            )}
          </div>
          <div>
            {fileContent && (
              <span>{fileContent.split('\n').length} lines</span>
            )}
          </div>
        </div>

        {/* Floating Search Button for Text Selection in Modal */}
        {selectionSearch.isVisible && (
          <FloatingSearchButton
            text={selectionSearch.text}
            usageCount={selectionSearch.usageCount}
            position={selectionSearch.position}
            onSearch={handleSelectionSearch}
            onClose={selectionSearch.handleDismiss}
            isInModal={true}
          />
        )}
      </div>
    </div>
  );
};
